import { effect, Injectable, signal, inject } from '@angular/core';
import isEqual from 'lodash-es/isEqual';
import { Decimal } from 'decimal.js-light';
import { ES6Parser, ES6StaticEval } from 'espression';
import { FormService } from './form-service.service';

@Injectable({
  providedIn: 'root',
})
export class FormBuilderService {
  private readonly parser = new ES6Parser();
  private readonly staticEval = new ES6StaticEval();
  private readonly astCache = new Map<string, any>();
  private readonly nonStandardFnCache = new Map<string, (model: any) => any>();
  private formService = inject(FormService);

  private getAst(condit: string) {
    let ast = this.astCache.get(condit);
    if (!ast) {
      ast = this.parser.parse(condit);
      this.astCache.set(condit, ast);
    }
    return ast;
  }

  clearExpressionCaches(): void {
    this.astCache.clear();
    this.nonStandardFnCache.clear();
  }

  isNumber(value) {
    if (typeof value === 'number' || typeof value === 'string') {
      const formatted = +this.removePercentMask(value);
      return !isNaN(formatted);
    } else {
      return false;
    }
  }

  alphabetizeGroups(a, b) {
    const groupA = a.label.toUpperCase();
    const groupB = b.label.toUpperCase();

    let comparison = 0;
    if (groupA > groupB) {
      comparison = 1;
    } else if (groupA < groupB) {
      comparison = -1;
    }
    return comparison;
  }

  removeMoneyMask(obj) {
    if (obj) {
      return Number(
        obj
          // eslint-disable-next-line no-useless-escape
          .replace(/\,/gi, '')
          .replace('$', '')
          .replace(/[\u2212\u2013\u2014]/g, '-') // replace any non-standard hyphen chars for negative numbers
      );
    }
  }

  removePercentMask(obj) {
    if (typeof obj === 'string' || obj instanceof String) {
      if (obj.includes('%,') || obj.includes('%, ')) {
        return obj.replace(/%/g, '').replace(/\s/g, '').trim();
      } else {
        const formatted = obj
          // eslint-disable-next-line no-useless-escape
          .replace(/\,/gi, '')
          .replace(/%/g, '')
          .replace(/\s/g, '');
        return Number(formatted) / 100;
      }
    }
    return Number(obj) / 100;
  }

  formatPercent(num, precision?) {
    const inpt = new Decimal(num);
    return inpt.times(100).todp(precision ? precision : 3);
  }

  deconstructPercent(num) {
    return (num / 1000) * 100;
  }

  getObjectDiff(obj1, obj2) {
    const diff = Object.keys(obj1).reduce((result, key) => {
      if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
        result.push(key);
      } else if (isEqual(obj1[key], obj2[key])) {
        const resultKeyIndex = result.indexOf(key);
        result.splice(resultKeyIndex, 1);
      }
      return result;
    }, Object.keys(obj2));

    return diff;
  }

  deDupObjectArray(data) {
    const uniqueArray = data.filter(
      (object, index) =>
        index ===
        data.findIndex(obj => JSON.stringify(obj) === JSON.stringify(object))
    );
    return uniqueArray;
  }

  cleanHtml(value) {
    const htmlRegEx = /<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g;
    if (!value || Boolean(value)) {
      return value;
    } else if (Array.isArray(value)) {
      const newValue = value.forEach(x => {
        return x.replace(htmlRegEx, '');
      });
      return newValue;
    } else {
      return value.replace(htmlRegEx, '');
    }
  }

  evalConditional(
    showEvalErrors,
    condit,
    passedModel?,
    passedField?,
    pause = false
  ) {
    if (condit) {
      if (showEvalErrors) {
        console.warn(`   `);
        console.warn(`***** ***** ***** ***** ***** ***** **** *****`);
        console.warn(
          `**** If you see an error after this, this conditional is broken: ${condit} ****`
        );
        console.warn(`***** ***** ***** ***** ***** ***** **** *****`);
        console.warn(`   `);
      }

      const varType = typeof condit;
      if (varType === 'boolean') {
        return condit;
      } else {
        const actualModel =
          passedModel && Object.keys(passedModel).length > 0
            ? passedModel
            : this.formService.getModel();
        let localModelData;

        if (passedModel) {
          localModelData = passedModel;
        } else if (passedField) {
          localModelData = passedField.model;
        }

        try {
          const ast = this.getAst(condit);
          const value = this.staticEval.evaluate(ast, {
            model: actualModel,
            root: this.formService.getModel(),
            mainModel: this.formService.getModel(),
            field: passedField,
            localModel: localModelData,
          });
          return value;
        } catch (err) {
          console.warn(`   `);
          console.warn(`***** ***** ***** ***** ***** ***** **** *****`);
          console.warn(`**** Can't process conditional: ${condit} ****`);
          console.warn(`**** ***** Processing Error: ${err} ***** ****`);
          console.warn(`***** ***** ***** ***** ***** ***** **** *****`);
          console.warn(`   `);
        }
      }
    }
    return false;
  }

  evalNonStandardConditional(condit) {
    const model = this.formService.getModel();
    let fn = this.nonStandardFnCache.get(condit);
    if (!fn) {
      fn = new Function('model', 'return (' + condit + ')') as (
        model: any
      ) => any;
      this.nonStandardFnCache.set(condit, fn);
    }
    return fn(model);
  }
}
